import type { CSSProperties } from 'react';

import {
  ELEMENT_CLASS_LABELS,
  ROOM_TEMPERATURE_C,
  periodicMapCells,
  type ConductionResult,
  type ElementClass,
} from './metalsNonmetals';

const GLOBE = { cx: 160, cy: 40, r: 16 };
const RAY_COUNT = 8;
const RAY_INNER = 20;
const RAY_OUTER = 27;

const GLOBE_FILL: Record<ConductionResult, string> = {
  good: 'fill-brand-sunshine',
  semiconductor: 'fill-wash-sunshine',
  poor: 'fill-white',
};

const GLOBE_TEXT: Record<ConductionResult, string> = {
  good: 'Globe lit brightly',
  semiconductor: 'Globe glows faintly at most',
  poor: 'Globe off',
};

/** Battery, globe and sample gap. The globe state is driven by the conductivity result. */
export function CircuitTester({ result, sample }: { result: ConductionResult | null; sample: string }) {
  const label = result ? `Circuit test with ${sample}: ${GLOBE_TEXT[result]}` : `Circuit test with ${sample}: not run yet`;
  return (
    <figure className="min-w-0">
      <svg viewBox="0 0 240 130" role="img" aria-label={label} className="mx-auto h-auto w-full max-w-xs">
        <path d="M40 40 L40 100 L95 100 M145 100 L200 100 L200 40 L176 40 M144 40 L40 40" className="fill-none stroke-ink" strokeWidth={2} />
        <line x1={34} y1={62} x2={46} y2={62} className="stroke-ink" strokeWidth={3} />
        <line x1={30} y1={70} x2={50} y2={70} className="stroke-ink" strokeWidth={2} />
        <text x={56} y={70} className="fill-slate2 text-[9px] font-bold">battery</text>
        <rect x={95} y={92} width={50} height={16} rx={3} className="fill-surface stroke-ink" strokeWidth={1.5} />
        <text x={120} y={122} textAnchor="middle" className="fill-ink text-[9px] font-bold">sample</text>
        {result === 'good'
          ? Array.from({ length: RAY_COUNT }, (_, index) => {
            const angle = (index / RAY_COUNT) * 2 * Math.PI;
            return (
              <line
                key={index}
                x1={GLOBE.cx + RAY_INNER * Math.cos(angle)}
                y1={GLOBE.cy + RAY_INNER * Math.sin(angle)}
                x2={GLOBE.cx + RAY_OUTER * Math.cos(angle)}
                y2={GLOBE.cy + RAY_OUTER * Math.sin(angle)}
                className="stroke-brand-sunshine"
                strokeWidth={2}
              />
            );
          })
          : null}
        <circle cx={GLOBE.cx} cy={GLOBE.cy} r={GLOBE.r} className={`${result ? GLOBE_FILL[result] : 'fill-white'} stroke-ink`} strokeWidth={2} />
        <path d={`M${GLOBE.cx - 6} ${GLOBE.cy + 4} L${GLOBE.cx - 3} ${GLOBE.cy - 4} L${GLOBE.cx} ${GLOBE.cy + 4} L${GLOBE.cx + 3} ${GLOBE.cy - 4} L${GLOBE.cx + 6} ${GLOBE.cy + 4}`} className="fill-none stroke-ink" strokeWidth={1.2} />
      </svg>
      <figcaption className="mt-1 text-center text-xs font-extrabold text-ink">
        {result ? GLOBE_TEXT[result] : 'Run the electrical test to see the globe'}
      </figcaption>
    </figure>
  );
}

const SCALE_MIN_C = -250;
const SCALE_MAX_C = 3750;
const SCALE_TICKS = [-200, 0, 1000, 2000, 3000];

function percentOf(celsius: number): number {
  const clamped = Math.min(SCALE_MAX_C, Math.max(SCALE_MIN_C, celsius));
  return ((clamped - SCALE_MIN_C) / (SCALE_MAX_C - SCALE_MIN_C)) * 100;
}

function formatCelsius(value: number): string {
  return `${value < 0 ? '−' : ''}${Math.abs(value)} °C`;
}

/** Melting point marker against room temperature, on a linear scale. */
export function MeltingScale({ name, meltingPointC }: { name: string; meltingPointC: number }) {
  const markerStyle: CSSProperties = { left: `${percentOf(meltingPointC)}%` };
  const roomStyle: CSSProperties = { left: `${percentOf(ROOM_TEMPERATURE_C)}%` };
  return (
    <figure className="min-w-0" aria-label={`${name} melts at ${formatCelsius(meltingPointC)}; room temperature is 25 °C`}>
      <div className="relative mx-2 mt-8 h-3 rounded-full bg-gradient-to-r from-wash-sky via-wash-sunshine to-wash-coral">
        <span className="absolute -top-7 -translate-x-1/2 whitespace-nowrap text-[10px] font-extrabold text-slate2" style={roomStyle}>
          25 °C room
        </span>
        <span className="absolute -top-1 h-5 w-0.5 -translate-x-1/2 bg-slate2" style={roomStyle} aria-hidden="true" />
        <span className="absolute -top-2 h-7 w-1.5 -translate-x-1/2 rounded-full bg-ink" style={markerStyle} aria-hidden="true" />
      </div>
      <div className="relative mx-2 mt-2 h-4 text-[10px] font-bold text-slate2" aria-hidden="true">
        {SCALE_TICKS.map((tick) => (
          <span key={tick} className="absolute -translate-x-1/2" style={{ left: `${percentOf(tick)}%` }}>
            {formatCelsius(tick)}
          </span>
        ))}
      </div>
      <figcaption className="mt-2 text-center text-xs font-extrabold text-ink">
        {name}: melting point {formatCelsius(meltingPointC)} (black marker)
      </figcaption>
    </figure>
  );
}

const CLASS_CELL: Record<ElementClass, string> = {
  metal: 'bg-wash-sky border-solid border-brand-sky/60',
  'non-metal': 'bg-wash-sunshine border-dotted border-ink/50',
  metalloid: 'bg-wash-mint border-dashed border-ink/60',
};

const GRID_STYLE: CSSProperties = { gridTemplateColumns: 'repeat(18, minmax(0, 1fr))' };

/** Periods 1–6 of the periodic table, generated from the shared dataset. */
export function PeriodicMap({
  selected,
  labSymbols,
  showClasses,
}: {
  selected: string;
  labSymbols: string[];
  showClasses: boolean;
}) {
  const cells = periodicMapCells();
  return (
    <figure className="min-w-0">
      <div className="grid gap-px" style={GRID_STYLE} role="list" aria-label="Periodic table position map">
        {cells.map((cell) => {
          const isSelected = cell.symbol === selected;
          const inLab = labSymbols.includes(cell.symbol);
          const classTone = showClasses && cell.elementClass ? CLASS_CELL[cell.elementClass] : 'bg-surface border-solid border-hairline';
          const classText = showClasses && cell.elementClass ? `, ${ELEMENT_CLASS_LABELS[cell.elementClass].toLowerCase()}` : '';
          return (
            <div
              key={cell.symbol}
              role="listitem"
              aria-label={`${cell.symbol}${classText}${isSelected ? ', selected' : ''}`}
              style={{ gridRow: cell.row, gridColumn: cell.column }}
              className={`flex aspect-square min-w-0 items-center justify-center overflow-hidden rounded-[3px] border text-[7px] leading-none sm:text-[10px] ${
                isSelected
                  ? 'z-10 scale-125 border-ink bg-ink font-extrabold text-white ring-2 ring-brand-coral'
                  : `${classTone} ${inLab ? 'font-extrabold text-ink' : 'text-slate2'}`
              }`}
            >
              {cell.symbol}
            </div>
          );
        })}
      </div>
      {showClasses ? (
        <figcaption className="mt-3 flex flex-wrap gap-2 text-xs font-bold text-ink-soft">
          {(Object.keys(CLASS_CELL) as ElementClass[]).map((elementClass) => (
            <span key={elementClass} className="inline-flex items-center gap-1.5">
              <span className={`inline-block h-3.5 w-3.5 rounded-sm border-2 ${CLASS_CELL[elementClass]}`} aria-hidden="true" />
              {ELEMENT_CLASS_LABELS[elementClass]} ({elementClass === 'metal' ? 'solid edge' : elementClass === 'metalloid' ? 'dashed edge' : 'dotted edge'})
            </span>
          ))}
        </figcaption>
      ) : (
        <figcaption className="mt-3 text-xs font-bold text-slate2">
          The highlighted cell is the element you are testing. Metal and non-metal regions appear after you classify.
        </figcaption>
      )}
    </figure>
  );
}
